import { AgentWorkflowDefinition } from "@/store/agentsStore";

/**
 * Node type classification for workflow nodes.
 */
export type WorkflowNodeType =
  | "AGENT"
  | "CONDITION"
  | "ACTION"
  | "MESSAGE"
  | "TRIGGER";

/**
 * Information about a node extracted from the workflow definition.
 */
export interface NodeInfo {
  type: WorkflowNodeType;
  actionId?: string;
}

/**
 * Retrieves node information from the workflow definition by looking up the nodeId.
 *
 * @param nodeId - The ID of the node to look up
 * @param workflowDefinition - The workflow definition containing nodes
 * @returns NodeInfo with type and optional actionId, or null if node not found or is a TRIGGER
 */
export function getNodeInfoFromDefinition(
  nodeId: string,
  workflowDefinition: AgentWorkflowDefinition | null | undefined,
): NodeInfo | null {
  if (!workflowDefinition?.nodes) {
    return null;
  }

  const node = workflowDefinition.nodes.find((n) => n.id === nodeId);

  // Triggers are the start of the workflow, not an executing step, so skip them
  if (!node || node.type === "TRIGGER") {
    return null;
  }

  return {
    type: node.type,
    actionId: node.actionId,
  };
}

/**
 * Returns the appropriate loading label based on node type.
 *
 * @param nodeType - The type of the workflow node
 * @param actionName - Optional action name to display (for ACTION nodes)
 * @returns A human-readable loading label
 */
export function getStepLoadingLabel(
  nodeType: WorkflowNodeType,
  actionName?: string,
): string {
  switch (nodeType) {
    case "AGENT":
      return actionName ? `Running ${actionName}...` : "Running agent...";
    case "CONDITION":
      return "Evaluating condition...";
    case "ACTION":
      return actionName ? `Running ${actionName}...` : "Running action...";
    case "MESSAGE":
      return "Sending message...";
    case "TRIGGER":
      return "Starting workflow...";
    default:
      return "Processing...";
  }
}
